import { Subscription, Tool } from "lib/types";
import { useState, useEffect, useMemo } from "react";
import { useApi } from "lib/hooks/useApi";
import { toast } from "sonner";
import { tools } from "lib/data";
import {
  PHONE_NUMBER_MAX_LENGTH,
  getPhoneDigitsAfterPaste,
  getPhoneNumberValidationError,
  handlePhoneDigitKeyDown,
  normalizePhoneDigits,
} from "lib/phoneNumber";
import { getLicenseId } from "lib/subscription";

interface SubCardProps {
  subscriptions: Subscription[];
  toolId?: Tool;
  onUpdateWhatsApp?: (
    subscriptionId: string,
    newNumber: string,
  ) => Promise<void>;
  onCancelSuccess?: (subscriptionId: string) => void;
  onKeepPlanSuccess?: (subscriptionId: string) => void;
  updatingIds?: Set<string>;
  cancellingIds?: Set<string>;
}

const cancellationReasons: Record<Tool, string[]> = {
  tgb: [
    "It was difficult to use",
    "I faced delivery or speed issues",
    "I use another bulk sender",
    "It was too complex for my use",
    "Other",
  ],
  tgv: [
    "It didn't download properly",
    "It was confusing or slow",
    "I don't need it anymore",
    "It was too complex for my use",
    "Other",
  ],
  tgt: [
    "It didn't translate accurately",
    "I don't need translation anymore",
    "It was confusing or slow",
    "I think it is too expensive",
    "Other",
  ],
};

export default function SubCard({
  subscriptions,
  toolId,
  onUpdateWhatsApp,
  onCancelSuccess,
  onKeepPlanSuccess,
  updatingIds = new Set(),
  cancellingIds = new Set(),
}: SubCardProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    string | null
  >(null);
  const [newWhatsAppNumber, setNewWhatsAppNumber] = useState<string>("");
  const [numberError, setNumberError] = useState<string>("");
  const [cancelSubscriptionId, setCancelSubscriptionId] = useState<
    string | null
  >(null);
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(
    new Set(),
  );
  const [additionalComment, setAdditionalComment] = useState("");
  const [submittingCancelId, setSubmittingCancelId] = useState<string | null>(
    null,
  );
  const [submittingKeepPlanId, setSubmittingKeepPlanId] = useState<
    string | null
  >(null);
  const { makeRequest, loading } = useApi(toolId || "tgb");

  const reasons = useMemo(() => {
    return toolId ? cancellationReasons[toolId] : [];
  }, [toolId]);

  const supportsNumberEdit = true;
  const numberLabel = "Telegram Number";
  const purchaseUrl = useMemo(() => {
    return tools.find((tool) => tool.id === toolId)?.purchaseUrl || "";
  }, [toolId]);

  const purchaseButton = purchaseUrl ? (
    <a
      href={purchaseUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm px-4 py-2 bg-[var(--theme-accent)] text-white rounded-md font-semibold hover:opacity-90 transition-opacity flex items-center gap-2 cursor-pointer shrink-0"
      title="Buy new subscription"
    >
      <i className="fas fa-shopping-cart"></i>
      Buy New Subscription
    </a>
  ) : null;

  const resetCancelForm = () => {
    setSelectedReasons(new Set());
    setAdditionalComment("");
  };

  const openCancelModal = (subscriptionId: string) => {
    resetCancelForm();
    setCancelSubscriptionId(subscriptionId);
  };

  const toggleReason = (reasonText: string) => {
    setSelectedReasons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reasonText)) {
        newSet.delete(reasonText);
      } else {
        newSet.add(reasonText);
      }
      return newSet;
    });
  };

  const handleCancelSubmit = async () => {
    if (!toolId || !cancelSubscriptionId) {
      toast.error("Missing required information");
      return;
    }

    if (selectedReasons.size === 0) {
      toast.error("Please select at least one reason for cancellation");
      return;
    }

    if (!additionalComment.trim()) {
      toast.error("Please provide additional comments");
      return;
    }

    setSubmittingCancelId(cancelSubscriptionId);

    try {
      const res = await makeRequest(`/cancel-subscription`, {
        method: "POST",
        body: JSON.stringify({
          selectedReasons: Array.from(selectedReasons),
          additionalComment: additionalComment.trim(),
          subscriptionId: cancelSubscriptionId,
        }),
      });

      if (res?.success) {
        toast.success(res.message || "Subscription cancelled successfully");
        onCancelSuccess?.(cancelSubscriptionId);
        resetCancelForm();
        setCancelSubscriptionId(null);
      }
    } finally {
      setSubmittingCancelId(null);
    }
  };

  const handleKeepPlan = async (subscriptionId: string) => {
    if (!toolId) {
      toast.error("Missing required information");
      return;
    }

    setSubmittingKeepPlanId(subscriptionId);

    try {
      const res = await makeRequest(`/reactivate-subscription`, {
        method: "POST",
        body: JSON.stringify({ subscriptionId }),
      });

      if (res?.success) {
        toast.success(res.message || "Subscription restored successfully");
        onKeepPlanSuccess?.(subscriptionId);
      }
    } finally {
      setSubmittingKeepPlanId(null);
    }
  };

  useEffect(() => {
    const hasOpenModal = Boolean(editingSubscriptionId || cancelSubscriptionId);
    if (!hasOpenModal) return;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [editingSubscriptionId, cancelSubscriptionId]);

  const copyToClipboard = (text: string, code: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(code);
    toast.success("Copied", { richColors: true });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const visibleSubscriptions = subscriptions;

  if (visibleSubscriptions.length === 0) {
    return (
      <>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-[var(--dash-text-strong)]">
            Manage Subscription
          </h2>
          {purchaseButton}
        </div>
        <div className="bg-[var(--dash-surface)] border border-[var(--dash-border)] p-6 rounded-xl">
          <p className="text-[var(--dash-text)] text-center">No subscriptions found.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-[var(--dash-text-strong)]">
          Manage Subscription (Found - {visibleSubscriptions.length})
        </h2>
        {purchaseButton}
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:gap-5 mb-8">
          {[...visibleSubscriptions].reverse().map((subscription) => {
            const licenseId = getLicenseId(subscription);
            const isCancelled =
              subscription.isCancelled || subscription.plan === "Cancelled";
            const canKeepPlan = Boolean(
              subscription.canUndoCancellation && toolId,
            );
            const isPendingCancellation = canKeepPlan;
            const isFullyCancelled = isCancelled && !isPendingCancellation;
            const isCancelling =
              submittingCancelId === subscription.subscriptionId ||
              cancellingIds.has(subscription.subscriptionId) ||
              (loading && cancelSubscriptionId === subscription.subscriptionId);
            const isKeepingPlan =
              submittingKeepPlanId === subscription.subscriptionId;
            const isUpdating = updatingIds.has(licenseId);
            const isExpired =
              subscription.expiryInDays <= 0 &&
              subscription.plan !== "Lifetime";
            const canCancel =
              !isCancelled &&
              subscription.plan !== "Cancelled" &&
              (subscription.plan === "Lifetime" ||
                subscription.expiryInDays > 0) &&
              !!toolId;

            return (
              <div
                key={licenseId}
                className={`bg-[var(--dash-surface)] border p-4 sm:p-5 rounded-xl w-full max-w-[520px] mx-auto ${
                  isCancelled ? "border-[#ff4757]/50" : "border-[var(--dash-border)]"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-6 pb-3 border-b border-[var(--dash-border)]">
                  <div>
                    <h3 className="text-lg text-[var(--dash-text-strong)] font-semibold font-mono break-all">
                      {subscription.subscriptionId}
                    </h3>
                    {isPendingCancellation && (
                      <p className="text-xs text-[#ff4757] mt-1">
                        Cancellation scheduled — access continues until validity
                        ends
                      </p>
                    )}
                    {isFullyCancelled && (
                      <p className="text-xs text-[#ff4757] mt-1">
                        This subscription has ended and cannot be restored
                      </p>
                    )}
                  </div>
                  {canCancel && (
                    <button
                      onClick={() =>
                        openCancelModal(subscription.subscriptionId)
                      }
                      disabled={isCancelling || isUpdating || isKeepingPlan}
                      className="text-xs px-3 py-1.5 border border-[#ff4757] text-[#ff4757] rounded-md hover:bg-[#ff4757] hover:text-[var(--dash-text-strong)] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                      title="Cancel Subscription"
                    >
                      {isCancelling ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-ban"></i>
                          Cancel
                        </>
                      )}
                    </button>
                  )}
                  {canKeepPlan && (
                    <button
                      onClick={() =>
                        handleKeepPlan(subscription.subscriptionId)
                      }
                      disabled={isKeepingPlan || isUpdating || isCancelling}
                      className="text-xs px-3 py-1.5 border border-[var(--theme-accent)] text-[var(--theme-accent)] rounded-md hover:bg-[var(--theme-accent)] hover:text-[var(--dash-text-strong)] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                      title="Keep Plan"
                    >
                      {isKeepingPlan ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Restoring...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-undo"></i>
                          Keep Plan
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="mb-5">
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                    Account Email
                  </label>
                  <div className="text-[var(--dash-text-strong)] font-medium flex items-center gap-2 break-all">
                    <i className="fas fa-envelope text-gray-500"></i>{" "}
                    {subscription.email || subscription.userEmail}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 mb-5">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                      Current Plan
                    </label>
                    <div className="text-[var(--dash-text-strong)] font-medium flex items-center gap-2 flex-wrap">
                      <span>{subscription.plan}</span>
                      {isPendingCancellation && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#ff4757]/15 text-[#ff4757] border border-[#ff4757]/30">
                          Cancelling
                        </span>
                      )}
                      {isFullyCancelled && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-gray-500/15 text-gray-400 border border-gray-500/30">
                          Cancelled
                        </span>
                      )}
                    </div>
                  </div>
                  {supportsNumberEdit && (
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2 flex items-center gap-2">
                        {numberLabel}
                        {subscription.remainingUpdates !== undefined && (
                          <span className="text-[9px] normal-case text-gray-400 font-normal">
                            ({subscription.remainingUpdates} remaining /{" "}
                            {subscription.updateCount} used)
                          </span>
                        )}
                      </label>
                      <div className="text-[var(--dash-text-strong)] font-medium flex items-center gap-2">
                        {subscription.phoneNumber ||
                          subscription.userNumber ||
                          subscription.whatsappNumber ||
                          "Not set"}
                        {updatingIds.has(licenseId) ? (
                          <i className="fas fa-spinner fa-spin text-[var(--theme-accent)] text-sm"></i>
                        ) : !isExpired ? (
                          <i
                            className="fas fa-pencil-alt text-gray-500 hover:text-[var(--theme-accent)] cursor-pointer transition-colors text-sm"
                            onClick={() => {
                              setEditingSubscriptionId(licenseId);
                              setNewWhatsAppNumber(
                                subscription.phoneNumber ||
                                  subscription.userNumber ||
                                  subscription.whatsappNumber ||
                                  "",
                              );
                              setNumberError("");
                            }}
                            title={`Edit ${numberLabel}`}
                          ></i>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                    Activation Code
                  </label>
                  <div className="bg-[var(--dash-input)] p-3 rounded-md border border-[var(--dash-border)] flex justify-between items-center group">
                    <span className="font-mono text-[var(--dash-text-strong)] tracking-widest uppercase">
                      {subscription.activationCode}
                    </span>
                    <i
                      className={`${copiedCode === subscription.activationCode ? "fas fa-check text-[var(--theme-accent)]" : "far fa-copy hover:text-[var(--theme-accent)]"} cursor-pointer transition-colors`}
                      title={
                        copiedCode === subscription.activationCode
                          ? "Copied"
                          : "Copy to clipboard"
                      }
                      onClick={() =>
                        copyToClipboard(
                          subscription.activationCode,
                          subscription.activationCode,
                        )
                      }
                    ></i>
                  </div>
                  {(
                    subscription.phoneNumber ||
                    subscription.userNumber ||
                    subscription.whatsappNumber
                  )?.trim() && (
                    <div className="mt-3 flex items-start gap-2">
                      <i className="fas fa-check text-[var(--theme-accent)] text-sm mt-0.5 shrink-0"></i>
                      <div>
                        <p className="text-[var(--theme-accent)] text-sm font-medium">
                          Already active for -{" "}
                          {subscription.phoneNumber ||
                            subscription.userNumber ||
                            subscription.whatsappNumber}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                      Last Payment Date
                    </label>
                    <div className="text-[var(--dash-text-strong)] font-medium">
                      {new Date(subscription.chargedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                      {isPendingCancellation
                        ? "Valid Until"
                        : isFullyCancelled
                          ? "Status"
                          : subscription.expiryInDays > 0
                            ? "Renews In"
                            : "Status"}
                    </label>
                    <div
                      className={`${
                        isPendingCancellation
                          ? "text-[#ffa502]"
                          : isFullyCancelled
                            ? "text-red-500"
                            : subscription.expiryInDays > 0
                              ? "text-[var(--theme-accent)]"
                              : "text-red-500"
                      } font-medium flex items-center gap-2`}
                    >
                      <i className="fas fa-clock"></i>
                      {isFullyCancelled && subscription.plan !== "Lifetime"
                        ? "Ended"
                        : subscription.plan === "Lifetime"
                          ? "Lifetime"
                          : subscription.expiryInDays > 0
                            ? `${subscription.expiryInDays} Days`
                            : "Expired"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {cancelSubscriptionId &&
        (() => {
          const subscription = subscriptions.find(
            (sub) => sub.subscriptionId === cancelSubscriptionId,
          );
          if (!subscription) return null;

          const isCurrentlyCancelling =
            submittingCancelId === cancelSubscriptionId ||
            cancellingIds.has(cancelSubscriptionId) ||
            (loading && !!cancelSubscriptionId);

          return (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget && !isCurrentlyCancelling) {
                  resetCancelForm();
                  setCancelSubscriptionId(null);
                }
              }}
            >
              <div className="themed-scrollbar bg-[var(--dash-surface)] border border-[var(--dash-border)] rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl relative">
                {isCurrentlyCancelling && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg bg-[var(--dash-surface)]/85">
                    <i className="fas fa-spinner fa-spin text-[#ff4757] text-3xl"></i>
                    <p className="text-sm text-gray-300">
                      Cancelling subscription...
                    </p>
                  </div>
                )}
                {/* Close Button */}
                <button
                  onClick={() => {
                    if (!isCurrentlyCancelling) {
                      resetCancelForm();
                      setCancelSubscriptionId(null);
                    }
                  }}
                  disabled={isCurrentlyCancelling}
                  className="absolute top-4 right-4 z-20 text-gray-400 hover:text-[var(--dash-text-strong)] transition-colors p-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close popup"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pr-8 text-[#ff4757]">
                  <i className="fas fa-exclamation-triangle text-xl"></i>
                  <h2 className="font-bold text-xl">Cancel Subscription</h2>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <div className="bg-[var(--dash-input)] border border-[var(--dash-border)] rounded-md p-4 space-y-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Subscription ID
                      </label>
                      <div className="text-[var(--dash-text-strong)] font-mono text-sm break-all">
                        {subscription.subscriptionId}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Account Email
                      </label>
                      <div className="text-[var(--dash-text-strong)] text-sm flex items-center gap-2">
                        <i className="fas fa-envelope text-gray-500 text-xs"></i>
                        {subscription.email || subscription.userEmail}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Plan
                      </label>
                      <div className="text-[var(--dash-text-strong)] text-sm">
                        {subscription.plan}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block">
                      Why do you want to cancel?
                    </label>
                    <div className="space-y-2 p-3 border border-[var(--dash-border)] rounded-md bg-[var(--dash-bg)]">
                      {reasons.map((reasonOption) => (
                        <label
                          key={reasonOption}
                          className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedReasons.has(reasonOption)}
                            onChange={() => toggleReason(reasonOption)}
                            disabled={isCurrentlyCancelling}
                            className="mt-0.5"
                          />
                          <span>{reasonOption}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block">
                      Additional comments
                    </label>
                    <textarea
                      value={additionalComment}
                      onChange={(e) => setAdditionalComment(e.target.value)}
                      rows={4}
                      className="w-full bg-[var(--dash-input)] border border-[var(--dash-border)] rounded-md px-3 py-2 text-[var(--dash-text-strong)] outline-none focus:border-[#ff4757] transition-colors text-sm"
                      placeholder="Please provide additional details about your cancellation"
                      disabled={isCurrentlyCancelling}
                    />
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg text-red-400 text-xs flex gap-2 items-start">
                    <i className="fas fa-exclamation-triangle mt-0.5"></i>
                    <p>
                      Are you sure you want to cancel this subscription? You can
                      continue using your plan until its validity ends, and you
                      can undo the cancellation anytime until then.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        resetCancelForm();
                        setCancelSubscriptionId(null);
                      }}
                      disabled={isCurrentlyCancelling}
                      className="flex-1 px-4 py-2 border border-[var(--dash-border)] text-gray-400 rounded-md hover:border-white hover:text-[var(--dash-text-strong)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Keep Subscription
                    </button>
                    <button
                      onClick={handleCancelSubmit}
                      disabled={
                        isCurrentlyCancelling ||
                        selectedReasons.size === 0 ||
                        !additionalComment.trim()
                      }
                      className="flex-1 px-4 py-2 bg-[#ff4757] text-[var(--dash-text-strong)] rounded-md font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isCurrentlyCancelling ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Cancelling...
                        </>
                      ) : (
                        "Cancel Subscription"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Edit linked number modal */}
      {supportsNumberEdit &&
        editingSubscriptionId &&
        (() => {
          const subscription = subscriptions.find(
            (sub) => getLicenseId(sub) === editingSubscriptionId,
          );
          if (!subscription) return null;

          const isCurrentlyUpdating = updatingIds.has(editingSubscriptionId);
          const isAddOnlyNumber = false;
          const numberActionLabel = isAddOnlyNumber ? "Add" : "Update";

          return (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget && !isCurrentlyUpdating) {
                  setEditingSubscriptionId(null);
                  setNewWhatsAppNumber("");
                  setNumberError("");
                }
              }}
            >
              <div className="themed-scrollbar bg-[var(--dash-surface)] border border-[var(--dash-border)] rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl relative">
                {/* Close Button */}
                <button
                  onClick={() => {
                    if (!isCurrentlyUpdating) {
                      setEditingSubscriptionId(null);
                      setNewWhatsAppNumber("");
                      setNumberError("");
                    }
                  }}
                  disabled={isCurrentlyUpdating}
                  className="absolute top-4 right-4 text-gray-400 hover:text-[var(--dash-text-strong)] transition-colors p-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close popup"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pr-8">
                  <i
                    className="fab fa-telegram text-[var(--theme-accent)] text-2xl"
                  ></i>
                  <h2 className="text-[var(--dash-text-strong)] font-bold text-xl">
                    {numberActionLabel} {numberLabel}
                  </h2>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  {/* Subscription Details */}
                  <div className="bg-[var(--dash-input)] border border-[var(--dash-border)] rounded-md p-4 space-y-3 mb-4">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Subscription ID
                      </label>
                      <div className="text-[var(--dash-text-strong)] font-mono text-sm break-all">
                        {subscription.subscriptionId}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Account Email
                      </label>
                      <div className="text-[var(--dash-text-strong)] text-sm flex items-center gap-2">
                        <i className="fas fa-envelope text-gray-500 text-xs"></i>
                        {subscription.email || subscription.userEmail}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                        Activation Code
                      </label>
                      <div className="text-[var(--dash-text-strong)] font-mono text-sm tracking-widest uppercase">
                        {subscription.activationCode}
                      </div>
                    </div>
                    {!isAddOnlyNumber &&
                      subscription.remainingUpdates !== undefined && (
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">
                          Updates Available
                        </label>
                        <div className="text-[var(--dash-text-strong)] text-sm">
                          <span className="text-[var(--theme-accent)] font-bold">
                            {subscription.remainingUpdates}
                          </span>{" "}
                          remaining /{" "}
                          <span className="text-gray-400">
                            {subscription.updateCount} used
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info Alert */}
                  {isAddOnlyNumber ? (
                    <div className="bg-[var(--theme-accent)]/10 border border-[var(--theme-accent)]/30 p-3 rounded-lg text-[var(--theme-accent)] text-xs flex gap-2 items-start">
                      <i className="fas fa-info-circle mt-0.5"></i>
                      <p>
                        Link a WhatsApp number once. After it is saved, it cannot
                        be changed from the dashboard.
                      </p>
                    </div>
                  ) : (
                    <>
                      {subscription.remainingUpdates !== undefined &&
                        subscription.remainingUpdates === 0 && (
                          <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg text-red-400 text-xs flex gap-2 items-start">
                            <i className="fas fa-exclamation-triangle mt-0.5"></i>
                            <p>
                              You have no remaining updates for this billing cycle.
                            </p>
                          </div>
                        )}

                      {subscription.remainingUpdates !== undefined &&
                        subscription.remainingUpdates > 0 && (
                          <div className="bg-[var(--theme-accent)]/10 border border-[var(--theme-accent)]/30 p-3 rounded-lg text-[var(--theme-accent)] text-xs flex gap-2 items-start">
                            <i className="fas fa-info-circle mt-0.5"></i>
                            <p>
                              You can change your registered number{" "}
                              <strong>
                                {subscription.remainingUpdates} more{" "}
                                {subscription.remainingUpdates === 1
                                  ? "time"
                                  : "times"}
                              </strong>
                            </p>
                          </div>
                        )}
                    </>
                  )}

                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                      {numberLabel}
                      <span className="text-gray-600 text-[10px] ml-1">
                        (with country code, without +)
                      </span>
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="tel"
                      value={newWhatsAppNumber}
                      onKeyDown={handlePhoneDigitKeyDown}
                      onPaste={(e) => {
                        setNewWhatsAppNumber(getPhoneDigitsAfterPaste(e));
                        if (numberError) setNumberError("");
                      }}
                      onChange={(e) => {
                        setNewWhatsAppNumber(
                          normalizePhoneDigits(e.target.value).slice(
                            0,
                            PHONE_NUMBER_MAX_LENGTH,
                          ),
                        );
                        if (numberError) setNumberError("");
                      }}
                      placeholder="e.g., 919876543210"
                      className={`w-full bg-[var(--dash-input)] rounded-md px-3 py-2 text-[var(--dash-text-strong)] outline-none transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                        numberError
                          ? "border border-red-500 focus:border-red-500"
                          : "border border-[var(--dash-border)] focus:border-[var(--theme-accent)]"
                      }`}
                      disabled={
                        isCurrentlyUpdating ||
                        subscription.remainingUpdates === 0
                      }
                      maxLength={PHONE_NUMBER_MAX_LENGTH}
                      aria-invalid={Boolean(numberError)}
                    />
                    {numberError && (
                      <p className="text-red-400 text-xs mt-2 flex gap-2 items-start">
                        <i className="fas fa-exclamation-circle mt-0.5"></i>
                        {numberError}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setEditingSubscriptionId(null);
                        setNewWhatsAppNumber("");
                        setNumberError("");
                      }}
                      disabled={isCurrentlyUpdating}
                      className="flex-1 px-4 py-2 border border-[var(--dash-border)] text-gray-400 rounded-md hover:border-white hover:text-[var(--dash-text-strong)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (
                          !newWhatsAppNumber.trim() ||
                          isCurrentlyUpdating ||
                          !editingSubscriptionId
                        )
                          return;

                        const validationError =
                          getPhoneNumberValidationError(newWhatsAppNumber);
                        if (validationError) {
                          setNumberError(validationError);
                          return;
                        }

                        try {
                          if (onUpdateWhatsApp) {
                            await onUpdateWhatsApp(
                              editingSubscriptionId,
                              newWhatsAppNumber.trim(),
                            );
                          }

                          setEditingSubscriptionId(null);
                          setNewWhatsAppNumber("");
                          setNumberError("");
                        } catch (error) {
                          console.error(
                            `Error updating ${numberLabel.toLowerCase()}:`,
                            error,
                          );
                        }
                      }}
                      disabled={
                        isCurrentlyUpdating || !newWhatsAppNumber.trim()
                      }
                      className="flex-1 px-4 py-2 bg-[var(--theme-accent)] text-white rounded-md font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isCurrentlyUpdating ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          {isAddOnlyNumber ? "Saving..." : "Updating..."}
                        </>
                      ) : (
                        numberActionLabel
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </>
  );
}
